import * as React from 'react'
import { LocaleSwitchLink, Footer as OriginalFooter_ } from '@betterplace/design-system/page-parts'

/**
 * @param {FooterProps} props
 * @returns
 */
function Footer({ text, localeSwitchLink, ...props }) {
  return React.createElement(OriginalFooter_, {
    ...props,
    text: {
      ...text,
      accessibilityStatement: React.createElement('div', {
        id: 'accessibility',
        dangerouslySetInnerHTML: {
          __html: text.accessibilityStatement,
        },
      }),
      organizationInfo: React.createElement('span', {
        dangerouslySetInnerHTML: {
          __html: text.organizationInfo,
        },
      }),
      organizationInfoDetails: React.createElement('span', {
        dangerouslySetInnerHTML: {
          __html: text.organizationInfoDetails,
        },
      }),
    },
    localeSwitchLink: React.createElement(LocaleSwitchLink, localeSwitchLink),
  })
}

/**
 * @typedef {Omit<import('react').ComponentProps<typeof OriginalFooter_>, 'Link' | 'Image'  | 'localeSwitchLink'> & {localeSwitchLink: {href: string, children: string, className?: string}}} FooterProps
 */

/**
 * @type {FooterProps}
 */
Footer.railsProps = {
  localeSwitchLink: {
    href: `<%= !(local_assigns[:disable_locale_switch] || local_assigns[:hide_locale_switch] || !request.get?) ? (@switch_locale_url.presence || switch_locale_url) : ''  %>`,
    children: `<%= !local_assigns[:hide_locale_switch] ? t('language.'+alternative_locale(I18n.locale).to_s, locale: alternative_locale(I18n.locale)) : '' %>`,
    className: 'language-switcher',
  },
  text: {
    organizationInfo: `<%= safe_html_translation_for_shared_components('.claim_html') %>`,
    organizationInfoDetails: `<%= safe_html_translation_for_shared_components('.adwords_html') %>`,
    cookieSettingsLink: {
      label: `<%= t('.cookie_banner_link') %>`,
      section: `<%= t('.headline_5') %>`,
    },
    linkedinLink: {
      label: `LinkedIn`,
      href: `<%= t('external_urls.linkedin_profile_url') %>`,
    },
    facebookLink: {
      label: `Facebook`,
      href: `<%= t('external_urls.facebook_page_url') %>`,
    },
    instagramLink: {
      label: `Instagram`,
      href: `<%=  t('external_urls.instagram_profile_url') %>`,
    },
    taxReceiptHint: {
      label: `<%= t('.tax_receipt_hint_link_label') %>`,
      href: `<%= t('external_urls.tax_receipt_hint_link_url', ) %>`,
      children: `<%= t('.tax_receipt_hint', tax_receipt_year: '0000', tax_receipt_processed_at_year: '0001') %>`,
    },
    transparentCivilSocietyLink: {
      label: `<%= t('.itz_logo_alt_text') %>`,
      href: `/c/ueber-uns/initiative-transparente-zivilgesellschaft`,
    },
    sections: [
      {
        title: `<%= t('.headline_1') %>`,
        column: 1,
        links: [
          {
            href: `<%= t('external_urls.about_us_path') %>`,
            children: `<%= t('.what_is_betterplace') %>`,
            external: true,
          },
          { href: `<%= t('external_urls.blog_path') %>`, children: `<%= t('.blog') %>`, external: true },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.academy_url') : '' %>`,
            children: `betterplace academy`,
            external: true,
          },
          { href: `<%= t('external_urls.team_path') %>`, children: `<%= t('.team') %>`, external: true },
          { href: `<%= t('external_urls.jobs_path') %>`, children: `<%= t('.jobs') %>`, external: true },
          { href: `<%= t('external_urls.press_path') %>`, children: `<%= t('.press') %>`, external: true },
          { href: `<%= t('external_urls.contact_us_path') %>`, children: `<%= t('.contact') %>`, external: true },
          {
            href: `<%= t('external_urls.a11y_statement_url') %>`,
            children: `<%= t('.a11y_statement') %>`,
            external: true,
          },
          { href: `<%= t('external_urls.api_url') %>`, children: `<%= t('.api') %>`, external: true },
        ],
      },
      {
        title: `<%= t('.headline_2') %>`,
        column: 2,
        links: [
          {
            href: `<%= landingpage_url(Landingpage::Fundraising) %>`,
            children: `<%= t('.landingpage_fundraising') %>`,
          },
          {
            href: `<%= t('external_urls.fundraising_events_path') %>`,
            children: `<%= t('.landingpage_fundraising_event') %>`,
          },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.fundraising_appeal_path') : '' %>`,
            children: `<%= t('.landingpage_fundraising_appeal') %>`,
          },
          { href: `<%= t('external_urls.solutions_with_employees_path') %>`, children: `<%= t('.as_a_company') %>` },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.streamer_path') : '' %>`,
            children: `<%= t('.as_a_streamer') %>`,
            external: true,
          },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.content_creator_path') : '' %>`,
            children: `<%= t('.as_a_content_creator') %>`,
            external: true,
          },
        ],
      },
      {
        title: `<%= t('.headline_3') %>`,
        column: 3,
        links: [
          { href: `<%= categories_url %>`, children: `<%= t('.project_search') %>` },
          {
            href: `<%= category_url(id: cc.projects.popular_category_id) %>`,
            children: `<%= t('.popular_projects') %>`,
          },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.donate_to_betterplace_path') : '' %>`,
            children: `<%= I18n.locale.to_s == 'de' ? t('.donate_to_betterplace') : '' %>`,
            external: true,
          },
        ],
      },
      {
        title: `<%= t('.headline_4') %>`,
        column: 3,
        links: [
          {
            href: `<%= user_signed_in? ? token_donation_overview_url(token_id: 'user', :anchor => 'donation-overview') : new_donation_overview_token_path %>`,
            children: `<%= t('.donation_overview') %>`,
          },
          {
            href: `<%= user_signed_in? ? token_donation_overview_url(token_id: 'user', :anchor => 'donation-receipts') : new_donation_overview_token_path %>`,
            children: `<%= t('.donation_receipts') %>`,
          },
          {
            href: `<%= user_signed_in? ? token_donation_overview_url(token_id: 'user', :anchor => 'recurring-donations') : new_donation_overview_token_path %>`,
            children: `<%= t('.donation_recurring') %>`,
          },
        ],
      },
      {
        title: `<%= t('.headline_5') %>`,
        column: 4,
        links: [
          {
            href: `<%= t('external_urls.help_url') %>`,
            children: `<%= t('.help') %>`,
            external: true,
          },
          { href: `<%= t('external_urls.legal_path') %>`, children: `<%= t('.legal') %>`, external: true },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.charity_streams_dos_and_donts_url') : '' %>`,
            children: `<%= I18n.locale.to_s == 'de' ? t('.charity_streams_dos_and_donts') : '' %>`,
            external: true,
          },
          {
            href: `<%= I18n.locale.to_s == 'de' ? t('external_urls.donation_gift_path') : '' %>`,
            children: `<%= I18n.locale.to_s == 'de' ? t('.donation_gift') : '' %>`,
            external: true,
          },
        ],
      },
    ],
  },
}
Footer.railsTemplateFile = 'views/layouts/_footer.html.erb'
export const OriginalFooter = OriginalFooter_

export default Footer
